import { TranslationFunction } from '../common/types'
import FAQTransport from '../transports/common/faq-transport'
import ListingSectionTransport, { ListingSectionObject } from '../transports/common/listing-section-transport'

export const buildAggregateRatingAndReviews = (topRatedObjects: ListingSectionTransport | null | undefined) => {
    if (!topRatedObjects) {
        return { aggregateRating: null, reviews: [] }
    }

    const aggregateRating =
        topRatedObjects.averageRating && topRatedObjects.totalRatingCount
            ? {
                  '@type': 'AggregateRating' as const,
                  bestRating: '5',
                  worstRating: '1',
                  ratingValue: String(Math.max(1, Math.min(5, Number(topRatedObjects.averageRating) || 3))),
                  reviewCount: topRatedObjects.totalRatingCount,
              }
            : null

    const reviews =
        topRatedObjects.objects
            ?.filter((obj: ListingSectionObject) => {
                const latestRating = obj.rating?.review?.latestRating
                return (
                    obj.rating?.average &&
                    obj.rating.average >= 1 &&
                    obj.rating.average <= 5 &&
                    latestRating?.ratingDate
                )
            })
            .slice(0, 12)
            .map((obj: ListingSectionObject) => {
                const latestRating = obj.rating?.review?.latestRating
                if (!latestRating || !latestRating.ratingDate || !obj.rating?.average) {
                    return null
                }

                const contact = latestRating.contact
                const authorName =
                    `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() ||
                    (contact?.lastName && contact?.address?.city
                        ? `${contact.lastName} from ${contact.address.city}`
                        : 'Anonymous')

                const review: any = {
                    '@type': 'Review',
                    reviewRating: {
                        '@type': 'Rating',
                        ratingValue: String(Math.max(1, Math.min(5, Number(obj.rating.average) || 3))),
                        bestRating: '5',
                    },
                    author: {
                        '@type': 'Person',
                        name: authorName,
                    },
                    datePublished: new Date(latestRating.ratingDate).toISOString().split('T')[0],
                }

                if (latestRating.travelDateFrom) {
                    review.contentReferenceTime = new Date(latestRating.travelDateFrom).toISOString().split('T')[0]
                }

                return review
            })
            .filter((review): review is NonNullable<typeof review> => review !== null) ?? []

    return { aggregateRating, reviews }
}

export const getOrganizationSchemaDetail = (t: TranslationFunction, topRatedObjects?: ListingSectionTransport) => {
    const schema: any = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+49 (0)421 - 146 29 600',
            contactType: 'customer service',
        },
        additionalType: 'WebSite',
        url: t('traumBaseUrl'),
        logo: `${t('traumBaseUrl')}/assets/_files/images/logo/tfw-logo-all.svg`,
        name: 'Traum-Ferienwohnungen',
        sameAs: [
            'https://www.facebook.com/TraumFerienwohnungen',
            'https://www.instagram.com/traumferienwohnungen/',
            'https://de.pinterest.com/traumfewo/',
            'https://twitter.com/traumfewo',
        ],
    }

    if (topRatedObjects) {
        const { aggregateRating, reviews } = buildAggregateRatingAndReviews(topRatedObjects)

        if (aggregateRating) {
            schema.aggregateRating = aggregateRating
        }

        if (reviews && reviews.length > 0) {
            schema.review = reviews
        }
    }

    return {
        __html: JSON.stringify(schema),
    }
}

export const getFAQSchemaHTML = (faqs: FAQTransport[] | undefined): { __html: string } | null => {
    if (!faqs || faqs.length === 0) {
        return null
    }

    const validFaqs = faqs.filter(faq => faq.question && faq.answer)

    if (validFaqs.length === 0) {
        return null
    }

    return {
        __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: validFaqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
        }),
    }
}
